<template>
  <div id="page_content">
    <header id="page_header">
      <div id="page_header_main">
        <div id="first_line">
          <div
            id="left_zone"
            class="flex-vcenter"
          >
            <ButtonClassic
              v-if="$route.meta.goBackBtn"
              v-tooltip="{
                content: $t('action.retour-page-precedente'),
                delay: { show: 800, },
              }"
              variant="special"
              color="primary"
              icon="left"
              @click="backToPreviousPage()"
            >
              <template #left-icon>
                <UilArrowLeft />
              </template>
            </ButtonClassic>
            <h1
              v-if="$route.meta.title || pageTitle"
              class="s3 bold"
            >
              <component :is="$route.meta.icon" />
              {{ $route.meta.title() || pageTitle }}
            </h1>
            <!-- @slot Emplacement pour des éléments à la suite du titre dans le header. -->
            <slot name="header-left" />
          </div>
          <div class="flex-vcenter">
            <AnchorsGroup
              v-if="navTitles && navTitles.length > 1"
              :nav-titles="navTitles"
              size="large"
              :nav-id="navTitlesId"
            />
            <div
              v-if="$slots['header-right']"
              id="header_right"
              class="flex-vcenter"
            >
              <!-- @slot Emplacement pour des éléments à la suite des ancres dans le header. -->
              <slot name="header-right" />
            </div>
          </div>
        </div>

        <div
          v-if="$slots['second-line']"
          id="second_line"
        >
          <!-- @slot Emplacement pour des éléments sous le titre de la page. -->
          <slot name="second-line" />
        </div>
      </div>

      <div
        v-if="$slots['action-bar']"
        id="action_bar"
      >
        <!-- @slot Emplacement pour des éléments dans "l'action bar" sous la zone principale. -->
        <slot name="action-bar" />
      </div>
    </header>

    <div id="content_page">
      <!-- @slot Contenu de la page. -->
      <slot name="content" />
    </div>

    <aside id="aside_content">
      <transition
        name="slide-fade"
        mode="out-in"
      >
        <!-- @slot Emplacement pour une sidebar (filtres). -->
        <slot name="aside-content" />
      </transition>
    </aside>
  </div>
</template>

<script>
import { defineComponent } from "vue";
import ButtonClassic from "../../components/buttons/ButtonClassic.vue";
import AnchorsGroup from "../../components/nav/AnchorsGroup.vue";

import { UilArrowLeft } from "@lde/vue-unicons";
import * as bootstrap from "bootstrap";

/**
 * Affiche le titre de la page avec l'icône associée, ainsi que son contenu. Il permet d'ajouter des
 * éléments supplémentaires dans le header (ancres, filtres, affichage...).
 * Utilise le Scrollspy de Bootstrap.
 */
export default defineComponent({
  name: "PageContent",
  components: {
    AnchorsGroup,
    ButtonClassic,
    UilArrowLeft,
  },
  props: {
    /**
     * Titres de la page. L'élément actif sera celui sur lequel l'utilisateur se trouve.
     * Il change donc selon la position du scroll dans la page.
     */
    navTitles: {
      type: Array,
      default: null,
    },
    /**
     * Surdéfinition de la route précédente. (Permet de contourner le fait de goBack sur une ancre)
     */
    prevPage: {
      type: Object,
      default: null,
    },
    /**
     * Définit un titre pour la page actuelle pour ne pas modifier le $route.meta.
     */
    pageTitle: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      prevScrollPos: 0,
      navTitlesId: "nav_titles",
    };
  },
  updated() {
    const scrollSpyEl = document.querySelector("#scroll_content");
    const target = document.querySelector(`#${this.navTitlesId}`);

    if (scrollSpyEl && target) {
      const scrollSpy = bootstrap.ScrollSpy.getInstance(scrollSpyEl);
      if (scrollSpy) {
        scrollSpy.dispose();
      }
      // eslint-disable-next-line
      new bootstrap.ScrollSpy(scrollSpyEl, { target });
    }
  },
  methods: {
    /**
     * Retourne à la page précédente ou sur la page d'accueil selon l'historique.
     */
    backToPreviousPage() {
      if (this.prevPage) {
        this.$router.push(this.prevPage);
      } else if (window.history.length > 2) {
        this.$router.go(-1);
      } else {
        this.$router.push("/");
      }
    },
  },
});
</script>

<style lang="scss">
@use "../../assets/styles/components/layout/page_content.scss";
</style>
